import { useEffect } from 'react';

/**
 * Illumination séquentielle de cartes au scroll, et tracé d'une ligne
 * « fil conducteur » synchronisé.
 *
 * Une seule progression P est calculée par grille : la carte i s'allume sur
 * P ∈ [i/n, (i+1)/n], si bien que chaque carte s'illumine au moment précis où
 * le trait l'atteint. Tout passe par la variable CSS --p (0 → 1), donc l'effet
 * fonctionne dans tous les navigateurs (pas de scroll-driven animations).
 *
 * @param {object} rootRef        ref du conteneur de la page
 * @param {Array}  groups         [[sélecteurGrille, sélecteurCarte, sélecteurLigne|null], ...]
 *                                Doit être une constante définie HORS du composant
 *                                (référence stable).
 * @param {Array}  deps           dépendances de re-calcul (langue, données…)
 */
export default function useScrollIlluminate(rootRef, groups, deps = []) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const resolved = groups
      .map(([gridSel, cardSel, lineSel]) => {
        const grid = root.querySelector(gridSel);
        if (!grid) return null;
        // Plusieurs tracés possibles : un horizontal et un vertical, dont
        // l'affichage dépend de la largeur d'écran (seul l'un est visible).
        const lines = lineSel ? Array.from(root.querySelectorAll(lineSel)) : [];
        return {
          grid,
          cards: Array.from(grid.querySelectorAll(cardSel)),
          lines,
          // Seul le tracé « glow » est piloté en tirets. Ses paths ne doivent PAS
          // porter d'attribut pathLength : les longueurs utilisées ici viennent de
          // getTotalLength(), et mélanger les deux échelles rend le masquage
          // incohérent (une portion de rouge restait visible au repos).
          dashPaths: lines.flatMap((l) => Array.from(l.querySelectorAll('path[class*="-glow"]'))),
        };
      })
      .filter(Boolean);
    if (!resolved.length) return undefined;

    const clamp = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);

    // Longueur du tracé, en unités du viewBox (indépendante de la taille rendue).
    // ATTENTION : getTotalLength() renvoie 0 sur un SVG en display:none — c'est le
    // cas du tracé horizontal en petit écran, et du vertical en grand écran. Une
    // valeur nulle ne doit donc JAMAIS être mise en cache ni écrite dans le style :
    // un stroke-dasharray à 0 signifie « trait continu » et le tracé resterait
    // affiché en entier, sans jamais se dessiner.
    const lengths = new WeakMap();
    const pathLength = (p) => {
      const cached = lengths.get(p);
      if (cached) return cached;
      const len = typeof p.getTotalLength === 'function' ? p.getTotalLength() : 0;
      if (len > 0) lengths.set(p, len);
      return len;
    };

    // prefers-reduced-motion : tout figé en état « allumé », aucun listener.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      resolved.forEach((g) => {
        g.cards.forEach((c) => c.style.setProperty('--p', '1'));
        g.lines.forEach((l) => l.style.setProperty('--p', '1'));
        g.dashPaths.forEach((p) => {
          const len = pathLength(p);
          if (!len) return;
          p.style.strokeDasharray = String(len + 6);
          p.style.strokeDashoffset = '0';
        });
      });
      return undefined;
    }

    // Progression 0 → 1 d'une grille : démarre quand elle apparaît en bas de
    // l'écran, se termine quand son bas atteint le milieu.
    //
    // La hauteur entre dans la course, sinon une grille plus haute que l'écran
    // (cartes empilées sur mobile) voit son tracé bouclé alors qu'on n'en a vu
    // que le début. Mais la course reste plus courte que la grille : la frontière
    // entre tracé et gris descend un peu plus vite que le regard, ce qui donne la
    // sensation d'un remplissage. Une course égale à la hauteur figerait cette
    // frontière au milieu de l'écran — c'est la page qui semblerait défiler.
    const groupProgress = (el) => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const course = Math.max(0.6 * vh, rect.height + 0.35 * vh);
      return clamp((0.85 * vh - rect.top) / course);
    };

    let raf = 0;
    const update = () => {
      raf = 0;
      for (const g of resolved) {
        const P = groupProgress(g.grid);
        const n = g.cards.length || 1;
        g.cards.forEach((c, i) => c.style.setProperty('--p', clamp(P * n - i).toFixed(3)));
        g.lines.forEach((l) => l.style.setProperty('--p', P.toFixed(3)));
        // Le tiret est exprimé dans la longueur RÉELLE du tracé plutôt qu'en
        // centièmes : avec vector-effect non-scaling-stroke sur un SVG étiré,
        // pathLength et le dash ne sont pas évalués dans le même espace, et le
        // trait s'arrêtait avant la fin.
        g.dashPaths.forEach((p) => {
          const len = pathLength(p);
          if (!len) return;   // tracé masqué : non mesurable, on laisse le repli CSS
          // Marge de 6 unités : si le motif de tirets fait exactement la longueur
          // du tracé, sa jonction tombe pile sur l'extrémité et stroke-linecap:
          // round y peint un point rouge, visible même à zéro.
          const dash = len + 6;
          p.style.strokeDasharray = String(dash);
          p.style.strokeDashoffset = String(dash * (1 - P));
        });
      }
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
